import React from 'react';
import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { motion, AnimatePresence } from 'framer-motion';
// import Image from './image';
import Action from './action';

const Accordion = ({
    i,
    icon,
    description,
    expanded,
    setExpanded,
    title,
    hideDescription,
}) => {
    const isOpen = i === expanded;

    return (
        <div
            onClick={() => setExpanded(i)}
            className={classNames(
                'AccordionList-item',
                isOpen
                    ? 'AccordionList-item--isOpen'
                    : 'AccordionList-item--isCollapsed'
            )}>
            {icon && <img src={icon} />}
            <div>
                <motion.header initial={false}>
                    <div className="AccordionList-title">
                        <h6>{title}</h6>
                    </div>
                </motion.header>
                {description && (
                    <>
                        {!hideDescription && (
                            <div className="AccordionList-description">
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: description,
                                    }}
                                />
                            </div>
                        )}
                        <AnimatePresence initial={false}>
                            {isOpen && hideDescription && (
                                <motion.section
                                    key="content"
                                    initial="collapsed"
                                    animate="open"
                                    exit="collapsed"
                                    variants={{
                                        open: { opacity: 1, height: 'auto' },
                                        collapsed: { opacity: 0, height: 0 },
                                    }}
                                    transition={{
                                        duration: 0.15,
                                        ease: 'easeIn',
                                    }}>
                                    <motion.div
                                        className="AccordionList-description"
                                        variants={{
                                            collapsed: { opacity: 0.5 },
                                            open: { opacity: 1 },
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: description,
                                        }}
                                        transition={{ duration: 0.2 }}>
                                    </motion.div>
                                </motion.section>
                            )}
                        </AnimatePresence>
                    </>
                )}
            </div>
        </div>
    );
};

export default function AccordionList({ block: { config, content } }) {
    const [expanded, setExpanded] = useState(0);
    const [ref, inView] = useInView({
        rootMargin: '-100px 0px',
    });
    let image = false;

    if (content.items) {
        image =
            expanded === false
                ? content.items[0].image
                : content.items[expanded].image;
    }

    if (!image && content.items[0].image) {
        image = content.items[0].image;
    }

    // Use a time if we have this checked
    // Only use it until the use has interacted
    useEffect(() => {
        if (config.cycle && inView) {
            let interval = null;
            interval = setInterval(() => {
                const nextItem =
                    content.items.length <= expanded + 1 ? 0 : expanded + 1;
                setExpanded(nextItem);
            }, 9500);

            return () => clearInterval(interval);
        }
    });

    const src = image?.src || image?.url;
    const imageCol = src && (
        <img
            className="AccodionList-image"
            src={src}
            alt={image.alt}
            style={{
                aspectRatio: `${image.width} / ${image.height}`
            }}
        />
    );

    const contentCol = (
        <>
            <h3 className="AccordionList-heading">{content.heading}</h3>
            {content.description && (
                <div
                    className="AccordionList-description mb2"
                    dangerouslySetInnerHTML={{ __html: content.description }}
                />
            )}
            {content.items &&
                content.items.map(({ icon, title, description }, i) => (
                    <Accordion
                        key={i}
                        i={i}
                        expanded={expanded}
                        setExpanded={setExpanded}
                        title={title}
                        description={description}
                        icon={icon.url}
                        hideDescription={config.hideDescription}
                    />
                ))}
            {content.actions && (
                <div className="AccordionList-actions">
                    {content.actions.map((action, key) => (
                        <div key={key}>
                            {' '}
                            <Action action={action} />{' '}
                        </div>
                    ))}
                </div>
            )}
        </>
    );

    const id = content.id ? content.id : '';

    return (
        <div
            id={id}
            ref={ref}
            className={classNames('container', config.className, {
                'AccordionList--animate': config.cycle,
            })}>
            <div
                className={classNames('AccordionList', {
                    'AccordionList--reverse': config.position === 'image_right',
                })}>
                <div
                    className={classNames(
                        'AccordionList__column',
                        {
                            'AccordionList__column--content': config.position === 'image_right',
                        }
                    )}>
                    {config.position === 'image_right' ? contentCol : imageCol}
                </div>
                <div
                    className={classNames(
                        'AccordionList__column',
                        {
                            'AccordionList__column--content': config.position !== 'image_right',
                        }
                    )}>
                    {config.position === 'image_right' ? imageCol : contentCol}
                </div>
            </div>
        </div>
    );
}
